import React, { useEffect, useState } from 'react';
import styles from './ShowOrganisationSetting.module.scss';
import { Chip, Grid, Typography, Box, CircularProgress } from '@mui/material';
import { Organization } from '../../../../../../types';
import { useOrganizationCrud } from 'core/hooks/useOrganizationCrud';
import DataRetentionSettings from '../DataRetentionSettings';

interface Day {
	id: number;
	label: string;
	locale: string;
	position: string;
	'@id': string;
}

interface DayPart {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
}

interface ShowOrganizationSettingProps {
	organization: Organization;
}

const ShowOrganizationSetting: React.FC<ShowOrganizationSettingProps> = ({ organization }) => {
	const [organizationDayParts, setOrganizationDayParts] = useState<DayPart[]>([]);
	const { dayParts, days, timeZones, fetchDayParts, fetchDays, fetchTimeZones } = useOrganizationCrud();

	// Fetch days and time zones on mount
	useEffect(() => {
		fetchDays();
		// also fetch time zones so we can display labels in the show view
		fetchTimeZones();
	}, [fetchDays, fetchTimeZones]);

	// Fetch day parts for the organization
	useEffect(() => {
		if (organization?.id) {
			fetchDayParts(organization.id);
		}
	}, [organization?.id, fetchDayParts]);

	// Transform API day parts to local DayPart format
	useEffect(() => {
		if (dayParts.length > 0) {
			const transformedDayParts: DayPart[] = dayParts.map((apiDayPart: any) => ({
				id: apiDayPart.id.toString(),
				name: apiDayPart.partName,
				startTime: apiDayPart.startTime,
				endTime: apiDayPart.endTime,
			}));
			setOrganizationDayParts(transformedDayParts);
		}
	}, [dayParts]);

	// Determine tenant id from localStorage if available, otherwise fall back to organization.id
	const tenantId = React.useMemo(() => {
		const orgId = localStorage.getItem('BYOD-organizationId');
		// fallback to organization id if nothing found in localStorage
		return orgId || organization?.id;
	}, [organization?.id]);

	if (!organization) {
		return <div className={styles.loading}><CircularProgress size={20} color="inherit" /></div>;
	}

	return (
		<div className={styles.showOrganizationSetting}>
			<div className={styles.formContainer}>
				{/* Name */}
				<div className={styles.formField}>
					<label className={styles.label}>Name</label>
					<input type="text" value={organization.name} readOnly className={styles.input} />
				</div>

				{/* Tenant Code (from localStorage) */}
				<div className={styles.formField}>
					<label className={styles.label}>Tenant Code</label>
					<input type="text" value={tenantId || 'N/A'} readOnly className={styles.input} />
				</div>

				{/* Organization Administrator */}
				<div className={styles.formField}>
					<label className={styles.label}>Organization Administrator</label>
					<input
						type="text"
						value={organization.organization_administrator?.name || 'N/A'}
						readOnly
						className={styles.input}
					/>
				</div>

				{/* Street Address */}
				<div className={styles.formField}>
					<label className={styles.label}>Street Address</label>
					<input
						type="text"
						value={organization.addressLine1 || 'N/A'}
						readOnly
						className={styles.input}
					/>
				</div>

				{/* City */}
				<div className={styles.formField}>
					<label className={styles.label}>City</label>
					<input type="text" value={organization.city || 'N/A'} readOnly className={styles.input} />
				</div>

				{/* State */}
				<div className={styles.formField}>
					<label className={styles.label}>State</label>
					<input
						type="text"
						value={organization.state || 'N/A'}
						readOnly
						className={styles.input}
					/>
				</div>

				{/* Postal Code */}
				<div className={styles.formField}>
					<label className={styles.label}>Postal Code</label>
					<input
						type="text"
						value={organization.postcode || 'N/A'}
						readOnly
						className={styles.input}
					/>
				</div>

				{/* Country */}
				<div className={styles.formField}>
					<label className={styles.label}>Country</label>
					<input
						type="text"
						value={organization.country || 'N/A'}
						readOnly
						className={styles.input}
					/>
				</div>

				{/* Time Zone */}
				<div className={styles.formField}>
					<label className={styles.label}>Time Zone</label>
					<input
						type="text"
						value={
							timeZones?.find((tz) => tz['@id'] === organization.timeZone)?.label || 'N/A'
						}
						readOnly
						className={styles.input}
					/>
				</div>

				{/* First Day of the Week */}
				<div className={styles.formField}>
					<label className={styles.label}>First Day of the Week</label>
					<input
						type="text"
						value={
							days?.find((day) => day['@id'] === organization.start_of_week)?.label || 'N/A'
						}
						readOnly
						className={styles.input}
					/>
				</div>

				{/* Products Licensed Section */}
				<Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', my: 2 }}>
					<Typography
						fontFamily={'Gotham'}
						color={'rgb(0 0 0 / 60%)'}
						variant="subtitle2"
						sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 2 }}
					>
						Products Licensed
					</Typography>

					{organization.products && organization.products.length > 0 ? (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
							{organization.products.map((product) => (
								<Chip key={product.id} label={product.label} variant="outlined" color="primary" />
							))}
						</Box>
					) : (
						<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
							No products licensed
						</Typography>
					)}
				</Box>

				{/* Business Day Configuration */}
				<Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', my: 2 }}>
					<Typography
						fontFamily={'Gotham'}
						color={'rgb(0 0 0 / 60%)'}
						variant="subtitle2"
						sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 2 }}
					>
						Business Day Configuration
					</Typography>

					{/* Start of Day & Start of Week */}
					<Grid container spacing={2} sx={{ mb: 2 }}>
						<Grid item xs={6}>
							<div className={styles.formField}>
								<label className={styles.label}>Start of Day</label>
								<input
									type="text"
									value={organization.start_of_day || '05:00'}
									readOnly
									className={styles.input}
								/>
							</div>
						</Grid>
						<Grid item xs={6}>
							<div className={styles.formField}>
								<label className={styles.label}>Start of Week</label>
								<input
									type="text"
									value={
										days?.find((day) => day['@id'] === organization.start_of_week)?.label || 'N/A'
									}
									readOnly
									className={styles.input}
								/>
							</div>
						</Grid>
					</Grid>

					{/* Day Parts */}
					<Box>
						<Typography
							variant="body1"
							fontFamily={'Gotham'}
							color={'rgb(0 0 0 / 60%)'}
							sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 2 }}
						>
							Day Parts
						</Typography>
						{organizationDayParts.length > 0 ? (
							<Grid container spacing={2} sx={{ mb: 2 }}>
								{organizationDayParts.map((dayPart) => (
									<Grid item xs={12} key={dayPart.id}>
										<div className={styles.formField}>
											<label className={styles.label}>Name</label>
											<input type="text" value={dayPart.name} readOnly className={styles.input} />
										</div>
										<Grid container spacing={2}>
											<Grid item xs={6}>
												<div className={styles.formField}>
													<label className={styles.label}>Start Time</label>
													<input
														type="text"
														value={dayPart.startTime}
														readOnly
														className={styles.input}
													/>
												</div>
											</Grid>
											<Grid item xs={6}>
												<div className={styles.formField}>
													<label className={styles.label}>End Time</label>
													<input
														type="text"
														value={dayPart.endTime}
														readOnly
														className={styles.input}
													/>
												</div>
											</Grid>
										</Grid>
									</Grid>
								))}
							</Grid>
						) : (
							<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
								No day parts defined
							</Typography>
						)}
					</Box>
				</Box>

				{/* Fiscal Calendar Configuration */}
				<Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '8px', my: 2 }}>
					<Typography
						fontFamily={'Gotham'}
						color={'rgb(0 0 0 / 60%)'}
						variant="subtitle2"
						sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 2 }}
					>
						Fiscal Calendar Configuration
					</Typography>

					<Grid container spacing={2}>
						<Grid item xs={6}>
							<div className={styles.formField}>
								<label className={styles.label}>Fiscal Start of Year</label>
								<input
									type="text"
									value={organization.fiscalStartOfYear || '01'}
									readOnly
									className={styles.input}
								/>
							</div>
						</Grid>
						<Grid item xs={6}>
							<div className={styles.formField}>
								<label className={styles.label}>Fiscal Calendar Type</label>
								<input
									type="text"
									value={organization.fiscalCalendarType || 'calendar'}
									readOnly
									className={styles.input}
								/>
							</div>
						</Grid>
						{organization.fiscalCalendarType === 'fiscal' && (
							<>
								<Grid item xs={6}>
									<div className={styles.formField}>
										<label className={styles.label}>Fiscal Period Type</label>
										<input
											type="text"
											value={organization.fiscalPeriodType || 12}
											readOnly
											className={styles.input}
										/>
									</div>
								</Grid>
								<Grid item xs={12}>
									<div className={styles.formField}>
										<label className={styles.label}>Fiscal Periods</label>
										<input
											type="text"
											value={
												organization.fiscalPeriods
													? organization.fiscalPeriods.join(', ')
													: Array(13).fill(4).join(', ')
											}
											readOnly
											className={styles.input}
										/>
									</div>
								</Grid>
							</>
						)}
					</Grid>
				</Box>

				<DataRetentionSettings canEdit={false} />
			</div>
		</div>
	);
};

export default ShowOrganizationSetting;
